import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { useCategories } from "@/components/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Monitor, Plus, Trash2, Copy, ExternalLink } from "lucide-react";

const copy = (url) => navigator.clipboard.writeText(url).then(() => toast.success("Lien copié"));

const LinkRow = ({ label, url, testId }) => (
  <div className="flex items-center gap-2">
    <span className="w-28 shrink-0 text-xs text-muted-foreground">{label}</span>
    <Input readOnly value={url} className="h-8 text-xs" data-testid={`${testId}-input`} />
    <Button size="icon" variant="secondary" className="h-8 w-8 shrink-0" onClick={() => copy(url)} data-testid={`${testId}-copy`}><Copy className="h-3.5 w-3.5" /></Button>
    <Button size="icon" variant="secondary" className="h-8 w-8 shrink-0" onClick={() => window.open(url, "_blank")} data-testid={`${testId}-open`}><ExternalLink className="h-3.5 w-3.5" /></Button>
  </div>
);

const fmt = (s) => (s >= 60 ? `${Math.floor(s / 60)} min ${String(Math.round(s % 60)).padStart(2, "0")} s` : `${Math.round(s)} s`);

const ScreenPreview = ({ screen }) => {
  const [slides, setSlides] = useState(null);
  const key = `${screen.id}:${(screen.category_ids || []).join(",")}`;
  useEffect(() => {
    setSlides(null);
    api.get("/public/tv-lite-data", { params: { ecran: screen.id } }).then(({ data }) => setSlides(data.slides)).catch(() => setSlides([]));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!slides) return <p className="text-xs text-muted-foreground">Chargement de l'aperçu…</p>;
  const total = slides.reduce((t, s) => t + (s.duration || 0), 0);
  return (
    <div className="space-y-1.5" data-testid={`screen-preview-${screen.id}`}>
      <p className="text-xs text-muted-foreground" data-testid={`screen-preview-count-${screen.id}`}>
        En ce moment : <b className="text-foreground">{slides.length} image{slides.length > 1 ? "s" : ""}</b>{slides.length > 0 && <> · cycle de {fmt(total)}</>}
      </p>
      {slides.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {slides.map((s, i) => (
            <img key={`${s.url}-${i}`} src={mediaUrl(s.url)} alt="" loading="lazy" title={`${s.duration} s`}
              className="h-12 w-20 shrink-0 rounded-md border border-border bg-black object-cover" data-testid={`screen-preview-thumb-${screen.id}-${i}`} />
          ))}
        </div>
      )}
    </div>
  );
};

const ScreenCard = ({ screen, categories, onSave, onDelete }) => {
  const [name, setName] = useState(screen.name);
  const [code, setCode] = useState(String(screen.code ?? ""));
  useEffect(() => setCode(String(screen.code ?? "")), [screen.code]);
  const saveCode = () => {
    const n = parseInt(code, 10);
    if (n >= 1 && n <= 999 && n !== screen.code) onSave({ ...screen, code: n }).then((ok) => !ok && setCode(String(screen.code ?? "")));
    else setCode(String(screen.code ?? ""));
  };
  const ids = screen.category_ids || [];
  const toggle = (id) => onSave({ ...screen, category_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] });
  const base = window.location.origin;
  return (
    <div className="space-y-3 rounded-xl border border-border p-4" data-testid={`screen-card-${screen.id}`}>
      <div className="flex items-center gap-2">
        <Monitor className="h-4 w-4 shrink-0 text-primary" />
        <span className="text-xs text-muted-foreground">N°</span>
        <Input type="number" min={1} max={999} value={code} onChange={(e) => setCode(e.target.value)} onBlur={saveCode}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="h-8 w-16 shrink-0 text-center font-bold" title="Numéro de l'écran (utilisé dans le lien)" data-testid={`screen-code-${screen.id}`} />
        <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== screen.name && onSave({ ...screen, name })}
          className="h-8 font-semibold" data-testid={`screen-name-${screen.id}`} />
        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-destructive" onClick={() => onDelete(screen)} data-testid={`screen-delete-${screen.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {categories.map((c) => {
          const on = ids.includes(c.id);
          return (
            <button key={c.id} onClick={() => toggle(c.id)} data-testid={`screen-${screen.id}-cat-${c.id}`}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${on ? "border-primary bg-primary/10 font-semibold" : "border-border text-muted-foreground opacity-60"}`}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />{c.name}
            </button>
          );
        })}
      </div>
      {!ids.length && <p className="text-xs text-amber-600" data-testid={`screen-empty-${screen.id}`}>Aucune catégorie cochée : seules les images sans catégorie seront affichées.</p>}
      <ScreenPreview screen={screen} />
      <LinkRow label="TV moderne" url={`${base}/ecran/${screen.code}`} testId={`screen-url-${screen.id}`} />
      <LinkRow label="TV ancienne" url={`${base}/api/ecran/${screen.code}`} testId={`screen-lite-url-${screen.id}`} />
    </div>
  );
};

export const ScreensSection = () => {
  const { categories } = useCategories();
  const [screens, setScreens] = useState([]);
  useEffect(() => { api.get("/screens").then(({ data }) => setScreens(data)); }, []);

  const run = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); return true; } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); return false; }
  };
  const add = () => run(async () => {
    const { data } = await api.post("/screens", { name: `Écran ${screens.length + 1}`, category_ids: categories.map((c) => c.id) });
    setScreens((s) => [...s, data]);
  }, "Écran ajouté");
  const save = (sc) => run(async () => {
    const { data } = await api.put(`/screens/${sc.id}`, { name: sc.name, category_ids: sc.category_ids, code: sc.code });
    setScreens((s) => s.map((x) => (x.id === sc.id ? data : x)));
  });
  const remove = (sc) => window.confirm(`Supprimer « ${sc.name} » ?`) && run(async () => {
    await api.delete(`/screens/${sc.id}`);
    setScreens((s) => s.filter((x) => x.id !== sc.id));
  }, "Écran supprimé");

  return (
    <section className="rounded-2xl border border-border bg-card p-6" data-testid="screens-section">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold"><Monitor className="h-5 w-5 text-primary" /> Écrans</h2>
        <Button size="sm" onClick={add} data-testid="add-screen-button"><Plus className="mr-1 h-4 w-4" /> Ajouter un écran</Button>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">Chaque écran affiche le bandeau en direct, limité aux catégories cochées. Les images sans catégorie apparaissent partout.</p>
      <div className="space-y-3">
        {screens.map((sc) => <ScreenCard key={sc.id} screen={sc} categories={categories} onSave={save} onDelete={remove} />)}
        {!screens.length && <p className="text-sm text-muted-foreground" data-testid="screens-empty">Aucun écran : toutes les TV affichent le bandeau complet.</p>}
      </div>
    </section>
  );
};
